// Emotion classification and intensity conversion utilities
// Color palette:
//   Positive moods: #4CAF50 (green)
//   Neutral moods:  #FFD54F (yellow)
//   Negative moods: #E57373 (red)

// Classify emotions as positive or negative
export const isPositiveEmotion = (emotion: string): boolean => {
  const negativeEmotions = [
    'guilty', 'frustrated', 'angry', 'anxious', 
    'sad', 'overwhelmed', 'fear', 'disgust', 
    'shame', 'grief', 'depression', 'nervous',
    'stressed', 'worried', 'upset', 'disappointed',
    'hopeless', 'jealous', 'hurt', 'annoyed'
  ];
  
  return !negativeEmotions.includes(emotion?.toLowerCase());
};

// Convert raw intensity to signed intensity based on emotion type
export const getSignedIntensity = (emotion: string, intensity: number): number => {
  if (!emotion || intensity === undefined || intensity === null) return 0;
  
  return isPositiveEmotion(emotion) ? intensity : -intensity;
};

// Get color based on signed intensity value
// Positive → green shades, Near-zero → yellow, Negative → red shades
export const getIntensityColor = (signedIntensity: number): string => {
  const abs = Math.abs(signedIntensity);

  if (abs <= 1) return '#FFD54F';         // neutral / low → yellow

  if (signedIntensity > 0) {
    // Positive moods: light green → deep green
    if (abs <= 3) return '#81C784';        // light green
    if (abs <= 6) return '#4CAF50';        // green (primary positive)
    return '#2E7D32';                      // deep green
  } else {
    // Negative moods: light red → deep red
    if (abs <= 3) return '#EF9A9A';        // light red
    if (abs <= 6) return '#E57373';        // red (primary negative)
    return '#C62828';                      // deep red
  }
};

// Dot border color for extra clarity on graphs
export const getDotStroke = (): string => '#333B44';

// Group emotion data by date
export const groupEmotionsByDate = (
  emotionData: Array<{
    timestamp: string;
    date: string;
    intensity: number;
    emotion: string;
  }>
) => {
  const groupedData: Record<string, Array<{
    intensity: number;
    signedIntensity: number;
    emotion: string;
    timestamp: string;
  }>> = {};

  // Sort data by timestamp first
  const sortedData = [...emotionData].sort((a, b) => 
    new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );

  // Group by date
  sortedData.forEach(entry => {
    const date = entry.date;
    if (!groupedData[date]) {
      groupedData[date] = [];
    }

    const signedIntensity = getSignedIntensity(entry.emotion, entry.intensity);
    
    groupedData[date].push({
      intensity: entry.intensity,
      signedIntensity: signedIntensity,
      emotion: entry.emotion,
      timestamp: entry.timestamp
    });
  });

  // Calculate average for each date and maintain chronological order
  return Object.entries(groupedData)
    .map(([date, entries]) => {
      const totalSignedIntensity = entries.reduce((sum, entry) => sum + entry.signedIntensity, 0);
      const averageSignedIntensity = totalSignedIntensity / entries.length;
      
      return {
        date,
        timestamp: entries[0].timestamp,
        averageIntensity: averageSignedIntensity,
        color: getIntensityColor(averageSignedIntensity),
        entries: entries.sort((a, b) => 
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        )
      };
    })
    .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
};
